import { Link } from 'react-router-dom';
import { useState } from 'react';
import { translations } from './App';
import './Dagpengerguide.css';

// ─── Guide content ───────────────────────────────────────────────────
// Norwegian + English. Other languages fall back to English so we can
// add them incrementally without breaking anything.
const guideContent = {
  no: {
    back: '← Tilbake til huben',
    category: 'NAV · Ytelser',
    title: 'Dagpenger',
    subtitle: 'Hva du må gjøre når du har mistet jobben — steg for steg, på under 10 minutter.',
    verified: 'Verifisert mot nav.no',
    readingTime: '8 min å lese',
    tocTitle: 'Innhold',
    toc: [
      { id: 'hva-er', label: 'Hva er dagpenger?' },
      { id: 'kvalifikasjoner', label: 'Har du rett på dagpenger?' },
      { id: 'trenger', label: 'Dette trenger du før du søker' },
      { id: 'steg', label: 'Slik søker du — steg for steg' },
      { id: 'meldekort', label: 'Meldekort hver 14. dag' },
      { id: 'feil', label: 'Vanlige feil å unngå' },
      { id: 'sporsmal', label: 'Ofte stilte spørsmål' },
      { id: 'kilder', label: 'Kilder og offisielle lenker' },
    ],
    sections: {
      hvaEr: {
        heading: 'Hva er dagpenger?',
        body: [
          'Dagpenger er en erstatning for tapt arbeidsinntekt når du er helt eller delvis arbeidsledig. NAV utbetaler dem hver 14. dag etter at du har sendt meldekort.',
          'Dagpenger er ikke sosialhjelp. Du har tjent dem opp gjennom tidligere arbeid, og det er en rettighet — ikke en gunst.',
        ],
      },
      kvalifikasjoner: {
        heading: 'Har du rett på dagpenger?',
        intro: 'Du må oppfylle alle disse kravene:',
        items: [
          { icon: '📉', title: 'Redusert arbeidstid', desc: 'Arbeidstiden din må være redusert med minst 50 %.' },
          { icon: '💼', title: 'Tidligere inntekt', desc: 'Du må ha hatt en viss arbeidsinntekt de siste 12 eller 36 månedene (grensen oppdateres årlig — sjekk nav.no).' },
          { icon: '🇳🇴', title: 'Opphold i Norge', desc: 'Du må være medlem av norsk folketrygd og oppholde deg i Norge.' },
          { icon: '🔍', title: 'Reell arbeidssøker', desc: 'Du må aktivt søke jobb og være villig til å ta hvilken som helst heltidsjobb hvor som helst i Norge.' },
          { icon: '📝', title: 'Registrert hos NAV', desc: 'Du må være registrert som arbeidssøker og sende meldekort hver 14. dag.' },
        ],
        note: 'Hvis du har sagt opp jobben selv uten gyldig grunn, kan du få ventetid på 12 uker før utbetaling starter.',
      },
      trenger: {
        heading: 'Dette trenger du før du søker',
        intro: 'Samle disse før du starter — det sparer deg for tid og frem-og-tilbake med NAV:',
        items: [
          'BankID (eller annen godkjent elektronisk ID for å logge inn på nav.no)',
          'Fødselsnummer eller D-nummer',
          'Kontonummer til norsk bankkonto',
          'Arbeidsavtale og oppsigelse / sluttdato fra forrige arbeidsgiver',
          'Lønnsslipper eller sluttoppgjør fra de siste 3 månedene',
          'CV (kan lastes opp senere, men bør være klar)',
        ],
      },
      steg: {
        heading: 'Slik søker du — steg for steg',
        steps: [
          {
            num: '1',
            title: 'Registrer deg som arbeidssøker',
            when: 'Samme dag eller dagen etter siste arbeidsdag',
            body: 'Gå til nav.no og velg «Registrer deg som arbeidssøker». Du trenger BankID. Dette må gjøres FØR du søker om dagpenger, ellers starter ikke utbetalingen på riktig dato.',
            tip: 'Du får ikke dagpenger for dagene før du registrerer deg. Vent ikke.',
          },
          {
            num: '2',
            title: 'Send søknad om dagpenger',
            when: 'Med en gang etter registrering',
            body: 'På nav.no, gå til «Søk om dagpenger». Fyll ut skjemaet digitalt. Du får umiddelbar bekreftelse på at søknaden er mottatt. Saksbehandlingstid er typisk 3–5 uker, men utbetalingen går tilbake til datoen du ble registrert.',
            tip: 'Vær ærlig med all info. Feil gir forsinkelser eller avslag.',
          },
          {
            num: '3',
            title: 'Last opp dokumentasjon',
            when: 'Innen 14 dager',
            body: 'NAV ber om dokumentasjon via Dine saker på nav.no — typisk arbeidsavtale, oppsigelse og siste lønnsslipper. Svar raskt. Manglende dokumenter er den vanligste årsaken til forsinket utbetaling.',
            tip: 'Du får varsel på SMS og e-post når NAV trenger noe.',
          },
          {
            num: '4',
            title: 'Send ditt første meldekort',
            when: 'Hver 14. dag, uten unntak',
            body: 'Meldekortet sier hvor mye du har jobbet, vært syk, eller vært på ferie. Selv uker der du ikke har jobbet i det hele tatt MÅ du sende meldekort. Glemmer du to meldekort på rad, stopper utbetalingen helt.',
            tip: 'Sett påminnelse i mobilen. NAV sender også varsel.',
          },
          {
            num: '5',
            title: 'Vent på vedtak og første utbetaling',
            when: '3–5 uker etter søknad',
            body: 'Du får et vedtak i posten eller digitalt. Der står dagpengegrunnlaget ditt (hvor mye du får per dag) og hvor lenge du har rett på dagpenger. Første utbetaling kommer typisk 2–3 dager etter at meldekortet er godkjent.',
            tip: 'Du kan klage på vedtaket hvis du er uenig — fristen er 6 uker.',
          },
        ],
      },
      meldekort: {
        heading: 'Meldekort hver 14. dag — dette er det viktigste',
        body: [
          'Meldekort er NAVs måte å vite at du fortsatt er arbeidssøker og hvor mye du har jobbet. Du må sende det hver 14. dag — selv om ingenting har endret seg.',
          'Du får en påminnelse på SMS og e-post når det er tid. Meldekortet skal sendes i perioden fredag til søndag etter en 14-dagers-periode.',
        ],
        rules: [
          { ok: true, text: 'Send meldekort selv i uker du ikke har jobbet' },
          { ok: true, text: 'Rapporter alle timer du har jobbet — selv få' },
          { ok: true, text: 'Oppgi ferie, sykdom og kurs' },
          { ok: false, text: 'Ikke glem to meldekort på rad — da stopper utbetalingen' },
          { ok: false, text: 'Ikke underrapporter timer — det regnes som svindel' },
        ],
      },
      feil: {
        heading: 'Vanlige feil å unngå',
        items: [
          { title: 'Å vente med å registrere seg', desc: 'Du får ikke dagpenger retroaktivt for dagene før du registrerer deg. Registrer deg samme dag som du mister jobben.' },
          { title: 'Å glemme meldekort', desc: 'To manglende meldekort på rad stopper utbetalingen. Du må da starte registreringen på nytt.' },
          { title: 'Å avslå jobber NAV tilbyr', desc: 'Som dagpengemottaker må du være villig til å ta hvilken som helst heltidsjobb. Å takke nei kan gi ventetid eller stans.' },
          { title: 'Å ikke oppgi småjobber', desc: 'Selv små frilansjobber eller vikariater må rapporteres på meldekortet. Dagpengene reduseres tilsvarende — men å skjule det er svindel.' },
          { title: 'Å ikke svare på NAV-henvendelser', desc: 'Sjekk Dine saker på nav.no jevnlig. Ubesvarte krav om dokumentasjon er den vanligste årsaken til avslag.' },
        ],
      },
      sporsmal: {
        heading: 'Ofte stilte spørsmål',
        items: [
          {
            q: 'Hvor mye får jeg i dagpenger?',
            a: 'Dagpengegrunnlaget er vanligvis rundt 62,4 % av din tidligere bruttoinntekt, men dette kan variere. Det er et tak (maks cirka 6G av folketrygdens grunnbeløp). Sjekk nav.no/dagpenger for dagens satser.',
          },
          {
            q: 'Hvor lenge kan jeg få dagpenger?',
            a: 'Normalt 52 eller 104 uker, avhengig av hvor høy inntekt du hadde før du ble ledig. Når perioden er over, må du ha vært i arbeid på nytt før du kan søke igjen.',
          },
          {
            q: 'Kan jeg studere mens jeg får dagpenger?',
            a: 'Som hovedregel nei. Men det finnes unntak for kortere kurs og etter søknad. Snakk med din veileder på NAV før du melder deg på noe.',
          },
          {
            q: 'Hva om jeg flytter til et annet EØS-land?',
            a: 'Du kan under visse vilkår ta med deg dagpengene til et annet EØS-land i inntil 3 måneder mens du leter etter jobb der. Dette må søkes om på forhånd.',
          },
          {
            q: 'Hva skjer hvis jeg får jobb igjen?',
            a: 'Rapporter det på neste meldekort. Hvis du fortsatt har redusert arbeidstid (f.eks. deltidsjobb), kan du fortsatt få delvise dagpenger. Er du i full jobb, stoppes dagpengene automatisk.',
          },
        ],
      },
      kilder: {
        heading: 'Kilder og offisielle lenker',
        intro: 'Alt innhold her er skrevet med utgangspunkt i offisielle NAV-kilder. Bekreft alltid datoer og satser direkte hos NAV før du tar avgjørelser.',
        links: [
          { label: 'NAV — Dagpenger (hovedsiden)', url: 'https://www.nav.no/dagpenger' },
          { label: 'NAV — Registrer deg som arbeidssøker', url: 'https://www.nav.no/arbeidssoker' },
          { label: 'NAV — Send meldekort', url: 'https://www.nav.no/meldekort' },
          { label: 'NAV — Satser og grunnbeløp', url: 'https://www.nav.no/grunnbelopet' },
        ],
      },
    },
    disclaimer: 'Dette er en veiledning som hjelper deg å forstå prosessen. Endelige vedtak gjøres av NAV, og regelverk kan endres. For bindende informasjon, se alltid nav.no eller kontakt NAV direkte.',
    cta: {
      primary: 'Gå til nav.no →',
      primaryUrl: 'https://www.nav.no/dagpenger',
      secondary: 'Tilbake til huben',
    },
    helpful: 'Var dette nyttig?',
    thanks: 'Takk for tilbakemeldingen!',
  },
  en: {
    back: '← Back to the hub',
    category: 'NAV · Benefits',
    title: 'Unemployment benefits (Dagpenger)',
    subtitle: 'What to do when you lose your job — step by step, in under 10 minutes.',
    verified: 'Verified against nav.no',
    readingTime: '8 min read',
    tocTitle: 'Contents',
    toc: [
      { id: 'hva-er', label: 'What are dagpenger?' },
      { id: 'kvalifikasjoner', label: 'Do you qualify?' },
      { id: 'trenger', label: 'What you need before applying' },
      { id: 'steg', label: 'How to apply — step by step' },
      { id: 'meldekort', label: 'Reporting every 14 days' },
      { id: 'feil', label: 'Common mistakes to avoid' },
      { id: 'sporsmal', label: 'Frequently asked questions' },
      { id: 'kilder', label: 'Sources and official links' },
    ],
    sections: {
      hvaEr: {
        heading: 'What are dagpenger?',
        body: [
          'Dagpenger is compensation for lost work income when you are fully or partly unemployed. NAV pays them every 14 days, after you submit a reporting form (meldekort).',
          'This is not social welfare. You have earned it through previous work, and it\'s a right — not a favour.',
        ],
      },
      kvalifikasjoner: {
        heading: 'Do you qualify?',
        intro: 'You must meet all of these:',
        items: [
          { icon: '📉', title: 'Reduced working hours', desc: 'Your working hours must be reduced by at least 50 %.' },
          { icon: '💼', title: 'Previous income', desc: 'You must have had a certain level of work income in the last 12 or 36 months (the threshold is updated yearly — check nav.no).' },
          { icon: '🇳🇴', title: 'Living in Norway', desc: 'You must be a member of Norwegian National Insurance and reside in Norway.' },
          { icon: '🔍', title: 'Genuine job seeker', desc: 'You must actively seek work and be willing to take any full-time job, anywhere in Norway.' },
          { icon: '📝', title: 'Registered with NAV', desc: 'You must register as a job seeker and submit a meldekort every 14 days.' },
        ],
        note: 'If you quit your job without a valid reason, you may get a 12-week waiting period before payments begin.',
      },
      trenger: {
        heading: 'What you need before applying',
        intro: 'Gather these before you start — it saves time and back-and-forth with NAV:',
        items: [
          'BankID (or another approved electronic ID for nav.no)',
          'Norwegian national ID number (fødselsnummer) or D-number',
          'Account number for a Norwegian bank account',
          'Employment contract and notice of dismissal / end date from your previous employer',
          'Payslips or final settlement from the last 3 months',
          'CV (can be uploaded later, but have it ready)',
        ],
      },
      steg: {
        heading: 'How to apply — step by step',
        steps: [
          {
            num: '1',
            title: 'Register as a job seeker',
            when: 'Same day or the day after your last working day',
            body: 'Go to nav.no and choose "Register as a job seeker". You need BankID. Do this BEFORE applying for dagpenger — otherwise your payments won\'t start on the right date.',
            tip: 'You do not get dagpenger for the days before you register. Don\'t wait.',
          },
          {
            num: '2',
            title: 'Submit the dagpenger application',
            when: 'Immediately after registering',
            body: 'On nav.no, go to "Apply for dagpenger". Fill out the form digitally. You get an instant receipt. Processing usually takes 3–5 weeks, but the payment will go back to your registration date.',
            tip: 'Be honest with everything. Errors cause delays or rejections.',
          },
          {
            num: '3',
            title: 'Upload documentation',
            when: 'Within 14 days',
            body: 'NAV requests documents via Dine saker on nav.no — typically your employment contract, notice, and last payslips. Respond quickly. Missing documents is the most common reason for delayed payments.',
            tip: 'You\'ll get SMS and email alerts when NAV needs something.',
          },
          {
            num: '4',
            title: 'Send your first meldekort',
            when: 'Every 14 days, no exceptions',
            body: 'The meldekort reports how much you\'ve worked, been sick, or been on holiday. Even in weeks you haven\'t worked at all, you MUST send it. Miss two in a row and your payments stop entirely.',
            tip: 'Set a phone reminder. NAV also sends alerts.',
          },
          {
            num: '5',
            title: 'Wait for the decision and first payment',
            when: '3–5 weeks after application',
            body: 'You receive a decision by post or digitally. It states your daily rate and how long you qualify. First payment typically arrives 2–3 days after your meldekort is approved.',
            tip: 'You can appeal if you disagree — the deadline is 6 weeks.',
          },
        ],
      },
      meldekort: {
        heading: 'Meldekort every 14 days — this is the most important part',
        body: [
          'The meldekort is NAV\'s way of knowing you\'re still a job seeker and how much you\'ve worked. You must send it every 14 days — even if nothing has changed.',
          'You\'ll get an SMS and email reminder. The form must be submitted Friday to Sunday after each 14-day period.',
        ],
        rules: [
          { ok: true, text: 'Send the meldekort even in weeks you didn\'t work' },
          { ok: true, text: 'Report every hour you did work — even small ones' },
          { ok: true, text: 'Declare holidays, illness, and courses' },
          { ok: false, text: 'Don\'t miss two in a row — payments stop' },
          { ok: false, text: 'Don\'t under-report hours — that counts as fraud' },
        ],
      },
      feil: {
        heading: 'Common mistakes to avoid',
        items: [
          { title: 'Waiting to register', desc: 'You don\'t get dagpenger retroactively for the days before you registered. Register the same day you lose your job.' },
          { title: 'Missing a meldekort', desc: 'Two missed meldekorts in a row stop your payments. You\'ll have to re-register.' },
          { title: 'Refusing jobs NAV offers', desc: 'As a dagpenger recipient you must be willing to take any full-time job. Saying no can trigger a waiting period or cancellation.' },
          { title: 'Not reporting small jobs', desc: 'Even short freelance gigs or temp work must appear on the meldekort. Your dagpenger are reduced accordingly — but hiding it counts as fraud.' },
          { title: 'Not responding to NAV messages', desc: 'Check Dine saker regularly. Ignored document requests are the leading cause of rejections.' },
        ],
      },
      sporsmal: {
        heading: 'Frequently asked questions',
        items: [
          {
            q: 'How much will I get?',
            a: 'The base is usually around 62.4 % of your previous gross income, with a cap (approximately 6× the National Insurance base amount). Check nav.no/dagpenger for the current rates.',
          },
          {
            q: 'How long can I receive dagpenger?',
            a: 'Typically 52 or 104 weeks, depending on your pre-unemployment income. Once the period is over, you must work again before reapplying.',
          },
          {
            q: 'Can I study while receiving dagpenger?',
            a: 'Generally no, but there are exceptions for short courses and with prior approval. Talk to your NAV advisor before enrolling in anything.',
          },
          {
            q: 'What if I move to another EEA country?',
            a: 'Under certain conditions you can take dagpenger with you to another EEA country for up to 3 months while job-hunting there. This must be applied for in advance.',
          },
          {
            q: 'What happens if I get a job again?',
            a: 'Report it on your next meldekort. If you still have reduced hours (e.g. part-time), you may get partial dagpenger. If you\'re working full-time, they stop automatically.',
          },
        ],
      },
      kilder: {
        heading: 'Sources and official links',
        intro: 'Everything here is based on official NAV sources. Always verify dates and rates directly with NAV before making decisions.',
        links: [
          { label: 'NAV — Dagpenger (main page)', url: 'https://www.nav.no/dagpenger' },
          { label: 'NAV — Register as a job seeker', url: 'https://www.nav.no/arbeidssoker' },
          { label: 'NAV — Submit meldekort', url: 'https://www.nav.no/meldekort' },
          { label: 'NAV — Rates and base amount (G)', url: 'https://www.nav.no/grunnbelopet' },
        ],
      },
    },
    disclaimer: 'This is a guide to help you understand the process. Final decisions are made by NAV, and regulations may change. For binding information, always refer to nav.no or contact NAV directly.',
    cta: {
      primary: 'Go to nav.no →',
      primaryUrl: 'https://www.nav.no/dagpenger',
      secondary: 'Back to the hub',
    },
    helpful: 'Was this helpful?',
    thanks: 'Thanks for your feedback!',
  },
};

function DagpengerGuide({ lang, setLang, menuOpen, setMenuOpen }) {
  // Map unsupported languages to English for now (structure is in place
  // for adding AR/PL/SO/UR/ES/TI later without changing the component).
  const g = guideContent[lang] || guideContent.en;
  const tNav = translations[lang]?.nav || translations.en.nav;

  const languages = [
    { code: '1f1ec-1f1e7', label: 'English',   key: 'en' },
    { code: '1f1f8-1f1e6', label: 'Arabic',    key: 'ar' },
    { code: '1f1f8-1f1f4', label: 'Somali',    key: 'so' },
    { code: '1f1f5-1f1f1', label: 'Polish',    key: 'pl' },
    { code: '1f1ea-1f1f7', label: 'Tigrinya',  key: 'ti' },
    { code: '1f1f5-1f1f0', label: 'Urdu',      key: 'ur' },
    { code: '1f1ea-1f1f8', label: 'Spanish',   key: 'es' },
    { code: '1f1f3-1f1f4', label: 'Norwegian', key: 'no' },
  ];
  const twemojiUrl = (code) =>
    `https://cdn.jsdelivr.net/gh/twitter/twemoji@14.0.2/assets/svg/${code}.svg`;

  const rtlLangs = ['ar', 'ur'];
  const dir = rtlLangs.includes(lang) ? 'rtl' : 'ltr';

  const [feedback, setFeedback] = useState(null); // null | 'yes' | 'no'

  const scrollTo = (id) => (e) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <div className="page guide-page" dir={dir}>

      {/* HEADER (same shell as landing) */}
      <header className="header">
        <Link to="/"><img src="/GI.png" alt="Global Integration logo" className="logo" /></Link>
        <nav className="nav">
          <Link to="/">{tNav.hub}</Link>
          <Link to="/#mission">{tNav.mission}</Link>
          <Link to="/#partners">{tNav.partners}</Link>
          <Link to="/#contact">{tNav.contact}</Link>
        </nav>
        <Link to="/" className="nav-cta">{tNav.cta}</Link>
        <button
          className={`hamburger ${menuOpen ? 'hamburger-open' : ''}`}
          onClick={() => setMenuOpen(!menuOpen)}
          aria-label="Toggle menu"
        >
          <span></span><span></span><span></span>
        </button>
      </header>

      {menuOpen && (
        <div className="mobile-nav" onClick={() => setMenuOpen(false)}>
          <Link to="/">{tNav.hub}</Link>
          <Link to="/#mission">{tNav.mission}</Link>
          <Link to="/#partners">{tNav.partners}</Link>
          <Link to="/#contact">{tNav.contact}</Link>
          <Link to="/" className="mobile-nav-cta">{tNav.cta}</Link>
        </div>
      )}

      {/* GUIDE HERO */}
      <section className="guide-hero">
        <div className="guide-hero-inner">
          <Link to="/" className="guide-back">{g.back}</Link>
          <div className="guide-category">{g.category}</div>
          <h1 className="guide-title">{g.title}</h1>
          <p className="guide-subtitle">{g.subtitle}</p>

          <div className="guide-meta">
            <span className="guide-meta-item">
              <span className="guide-meta-dot"></span>
              {g.verified}
            </span>
            <span className="guide-meta-sep">·</span>
            <span className="guide-meta-item">{g.readingTime}</span>
          </div>

          {/* Language chooser — compact form */}
          <div className="guide-langs">
            {languages.map((l) => (
              <button
                key={l.key}
                className={`flag-btn${lang === l.key ? ' flag-btn-active' : ''}`}
                onClick={() => setLang(l.key)}
                title={l.label}
                aria-label={`Switch to ${l.label}`}
              >
                <img src={twemojiUrl(l.code)} alt={l.label} className="flag-img" />
              </button>
            ))}
          </div>
          {lang !== 'no' && lang !== 'en' && (
            <p className="guide-lang-note">
              {lang === 'ar' || lang === 'ur'
                ? 'هذا الدليل متوفر بالإنجليزية حالياً — نعمل على ترجمته.'
                : 'This guide is currently shown in English while we translate it.'}
            </p>
          )}
        </div>
      </section>

      {/* GUIDE BODY: sidebar TOC + article */}
      <section className="guide-body">
        <div className="guide-body-inner">
          <aside className="guide-toc">
            <div className="guide-toc-label">{g.tocTitle}</div>
            <ul>
              {g.toc.map((item) => (
                <li key={item.id}>
                  <a href={`#${item.id}`} onClick={scrollTo(item.id)}>{item.label}</a>
                </li>
              ))}
            </ul>

            <div className="guide-toc-cta">
              <a
                href={g.cta.primaryUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary btn-primary-full"
              >
                {g.cta.primary}
              </a>
            </div>
          </aside>

          <article className="guide-article">

            {/* Hva er */}
            <section id="hva-er" className="guide-section">
              <h2>{g.sections.hvaEr.heading}</h2>
              {g.sections.hvaEr.body.map((p, i) => <p key={i}>{p}</p>)}
            </section>

            {/* Kvalifikasjoner */}
            <section id="kvalifikasjoner" className="guide-section">
              <h2>{g.sections.kvalifikasjoner.heading}</h2>
              <p className="guide-intro">{g.sections.kvalifikasjoner.intro}</p>
              <div className="qualify-grid">
                {g.sections.kvalifikasjoner.items.map((it, i) => (
                  <div key={i} className="qualify-card">
                    <span className="qualify-icon">{it.icon}</span>
                    <strong>{it.title}</strong>
                    <p>{it.desc}</p>
                  </div>
                ))}
              </div>
              <div className="guide-note guide-note-warn">
                <span className="guide-note-icon">⚠️</span>
                <p>{g.sections.kvalifikasjoner.note}</p>
              </div>
            </section>

            {/* Trenger */}
            <section id="trenger" className="guide-section">
              <h2>{g.sections.trenger.heading}</h2>
              <p className="guide-intro">{g.sections.trenger.intro}</p>
              <ul className="guide-checklist">
                {g.sections.trenger.items.map((item, i) => (
                  <li key={i}>
                    <span className="check-box">✓</span>
                    {item}
                  </li>
                ))}
              </ul>
            </section>

            {/* Steg */}
            <section id="steg" className="guide-section">
              <h2>{g.sections.steg.heading}</h2>
              <div className="steps-list">
                {g.sections.steg.steps.map((s) => (
                  <div key={s.num} className="step-card">
                    <div className="step-number">{s.num}</div>
                    <div className="step-body">
                      <div className="step-when">{s.when}</div>
                      <h3>{s.title}</h3>
                      <p>{s.body}</p>
                      <div className="step-tip">
                        <span className="step-tip-icon">💡</span>
                        {s.tip}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Meldekort */}
            <section id="meldekort" className="guide-section guide-section-highlight">
              <h2>{g.sections.meldekort.heading}</h2>
              {g.sections.meldekort.body.map((p, i) => <p key={i}>{p}</p>)}
              <div className="rules-grid">
                {g.sections.meldekort.rules.map((r, i) => (
                  <div key={i} className={`rule ${r.ok ? 'rule-ok' : 'rule-no'}`}>
                    <span>{r.ok ? '✓' : '✕'}</span>
                    {r.text}
                  </div>
                ))}
              </div>
            </section>

            {/* Feil */}
            <section id="feil" className="guide-section">
              <h2>{g.sections.feil.heading}</h2>
              <div className="mistakes-list">
                {g.sections.feil.items.map((it, i) => (
                  <div key={i} className="mistake">
                    <div className="mistake-x">✕</div>
                    <div>
                      <strong>{it.title}</strong>
                      <p>{it.desc}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* FAQ */}
            <section id="sporsmal" className="guide-section">
              <h2>{g.sections.sporsmal.heading}</h2>
              <div className="faq-list">
                {g.sections.sporsmal.items.map((item, i) => (
                  <FAQItem key={i} q={item.q} a={item.a} />
                ))}
              </div>
            </section>

            {/* Sources */}
            <section id="kilder" className="guide-section guide-section-sources">
              <h2>{g.sections.kilder.heading}</h2>
              <p>{g.sections.kilder.intro}</p>
              <ul className="sources-list">
                {g.sections.kilder.links.map((l, i) => (
                  <li key={i}>
                    <a href={l.url} target="_blank" rel="noopener noreferrer">
                      {l.label} <span className="sources-arrow">↗</span>
                    </a>
                  </li>
                ))}
              </ul>
            </section>

            {/* Disclaimer + final CTA */}
            <div className="guide-disclaimer">
              <p>{g.disclaimer}</p>
            </div>

            <div className="guide-end-cta">
              <a
                href={g.cta.primaryUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="btn-primary"
              >
                {g.cta.primary}
              </a>
              <Link to="/" className="btn-ghost btn-ghost-dark">
                ← {g.cta.secondary}
              </Link>
            </div>

            {/* Helpful feedback */}
            <div className="guide-feedback">
              {!feedback ? (
                <>
                  <span>{g.helpful}</span>
                  <button onClick={() => setFeedback('yes')}>👍</button>
                  <button onClick={() => setFeedback('no')}>👎</button>
                </>
              ) : (
                <span className="guide-feedback-thanks">{g.thanks}</span>
              )}
            </div>

          </article>
        </div>
      </section>

      {/* Minimal footer */}
      <footer className="footer">
        <div className="footer-inner">
          <div className="footer-bottom-bar">
            <p>© {new Date().getFullYear()} Global Integration. All rights reserved.</p>
            <p className="footer-bottom-note">{translations[lang]?.footerNote || translations.en.footerNote}</p>
          </div>
        </div>
      </footer>

    </div>
  );
}

// ─── Collapsible FAQ item ────────────────────────────────────────────
function FAQItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div className={`faq-item ${open ? 'faq-item-open' : ''}`}>
      <button
        type="button"
        className="faq-question"
        onClick={() => setOpen(!open)}
        aria-expanded={open}
      >
        <span>{q}</span>
        <span className="faq-chevron">{open ? '–' : '+'}</span>
      </button>
      {open && <div className="faq-answer">{a}</div>}
    </div>
  );
}

export default DagpengerGuide;